/**
 * Componente: campo numérico en milímetros del panel "Posición y tamaño" del Diseñador.
 *
 * - Muestra el valor del elemento (siempre al día: si se arrastra el cuadro con el mouse, el campo se actualiza solo)
 *   o queda vacío con el texto "Mixto" cuando los elementos seleccionados no coinciden.
 * - Mientras se escribe, lo tipeado es un borrador (no se toca el diseño): se aplica con Enter o al salir del campo.
 *   Esc descarta lo escrito. Las flechas arriba/abajo suman o restan 0,1 mm (1 mm con Shift) y aplican en el acto.
 * - Acepta coma o punto. No deja escribir letras, signos ni más de un separador: un "-" se descarta y avisa que no hay
 *   negativos. La validación de fondo (límites del lienzo) la hace posicionTamano.js a través de onConfirmar, que devuelve
 *   el aviso a mostrar (o null si salió todo bien).
 */

import React, { useState, useRef } from 'react';
import { formatearMedida, parsearMedida } from '../utils/posicionTamano';

const PASO_MM_CAMPO = 0.1;
const PASO_MM_CAMPO_SHIFT = 1;

const CampoMedida = ({ id, etiqueta, valorMm, mixto, onConfirmar, deshabilitado = false }) => {
  const [borrador, setBorrador] = useState(null); // null = no se está editando
  const [aviso, setAviso] = useState('');
  const descartarRef = useRef(false);
  // Si se tipeó un signo menos, el valor se rechaza al confirmar (en vez de aplicar el número sin su signo, que sería otra cosa).
  const negativoRef = useRef(false);

  const textoDelElemento = mixto || valorMm === null || valorMm === undefined ? '' : formatearMedida(valorMm);
  const editando = borrador !== null;
  const valorMostrado = editando ? borrador : textoDelElemento;

  const confirmar = (texto) => {
    const mensaje = onConfirmar(texto);
    setAviso(mensaje || '');
  };

  const alCambiar = (e) => {
    const crudo = e.target.value;
    // El aviso queda a la vista hasta que se confirme o se vuelva a entrar al campo (no se borra con la tecla siguiente).
    if (/[-−–]/.test(crudo)) {
      negativoRef.current = true;
      setAviso('No se permiten valores negativos.');
    } else if (/[^0-9.,\s]/.test(crudo)) setAviso('Escribí solo números, con coma o punto para los decimales.');
    // Se descartan los caracteres que no pueden formar parte de una medida.
    setBorrador(crudo.replace(/[^0-9.,]/g, ''));
  };

  const alSalir = () => {
    if (descartarRef.current) {
      descartarRef.current = false;
      setBorrador(null);
      return;
    }
    if (negativoRef.current) {
      // Se escribió un valor negativo: no se aplica nada y el campo vuelve al valor del elemento.
      negativoRef.current = false;
      setAviso('No se permiten valores negativos: el elemento quedó como estaba.');
      setBorrador(null);
      return;
    }
    if (borrador !== null && borrador !== textoDelElemento) confirmar(borrador);
    setBorrador(null);
  };

  const alApretarTecla = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      descartarRef.current = true;
      negativoRef.current = false;
      setAviso('');
      e.currentTarget.blur();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const actual = parsearMedida(borrador !== null ? borrador : textoDelElemento);
      const base = actual.ok ? actual.valor : (Number.isFinite(valorMm) ? valorMm : 0);
      const paso = e.shiftKey ? PASO_MM_CAMPO_SHIFT : PASO_MM_CAMPO;
      const siguiente = Math.max(0, base + (e.key === 'ArrowUp' ? paso : -paso));
      setBorrador(null);
      confirmar(String(Math.round(siguiente * 100) / 100));
    }
  };

  return (
    <div className="campo-medida">
      <label htmlFor={id}>{etiqueta}</label>
      <div className="campo-medida-caja">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          className="padron-input campo-medida-input"
          value={valorMostrado}
          placeholder={mixto ? 'Mixto' : ''}
          disabled={deshabilitado}
          onFocus={(e) => { setAviso(''); negativoRef.current = false; e.target.select(); }}
          onChange={alCambiar}
          onBlur={alSalir}
          onKeyDown={alApretarTecla}
          aria-describedby={aviso ? `${id}-aviso` : undefined}
        />
        <span className="campo-medida-unidad" aria-hidden="true">mm</span>
      </div>
      {aviso && (
        <span id={`${id}-aviso`} className="campo-medida-aviso" role="status">{aviso}</span>
      )}
    </div>
  );
};

export default CampoMedida;
